import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog as ScrollableDialog,
  ScrollableDialogContent,
  DialogHeader as ScrollableDialogHeader,
  DialogTitle as ScrollableDialogTitle,
  DialogFooter as ScrollableDialogFooter,
} from '@/components/ui/scrollable-dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FloatingDrawer,
  FloatingDrawerBody,
  FloatingDrawerFooter,
  FloatingDrawerHeader,
} from '@/components/ui/floating-drawer';
import { useFloatingDrawerResize } from '@/components/ui/floating-drawer-resize';
import { COMMERCE_V2_DRAWER_WIDTH } from '@/components/commerce-v2/shared';
import {
  ShopifyProductFormFields,
  emptyShopifyVariant,
  shopifyVariantProblem,
  useShopifyCategoryField,
  useShopifyTaxonomyCategoryField,
  type ShopifyImageEntry,
  type ShopifyProductForm,
  type ShopifyVariant,
} from './shopifyProductForm';
import { validateProductImageUrl } from '@/lib/commerce/productImageUrl';
import { shopifyVariantsPayload } from '@/lib/commerce/storeProductPayload';
import {
  descriptionToHtml,
  seedDescriptionForEditing,
} from '@/lib/commerce/productDescriptionHtml';
import { Loader2, Save, Package } from 'lucide-react';
import { shopifyApi, getCurrencySymbol } from '@/lib/api/services/shopify';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { ToastAction } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

type InventoryFailureReason =
  | 'missing_shopify_scope'
  | 'shopify_unauthorized'
  | 'no_shopify_location'
  | 'missing_inventory_item_id'
  | 'shopify_rejected'
  | 'network_error'
  | string;

interface FriendlyFailureMessage {
  title: string;
  description: string;
  offerReconnect?: boolean;
}

const mapInventoryFailureToFriendlyMessage = (
  reason: InventoryFailureReason,
  failureCount: number,
): FriendlyFailureMessage => {
  const variantWord = failureCount > 1 ? `${failureCount} variants` : 'this variant';
  switch (reason) {
    case 'missing_shopify_scope':
      return {
        title: "Couldn't update stock on Shopify",
        description:
          `Your Shopify app isn't allowed to change inventory, so stock for ${variantWord} was left as it is. ` +
          `Other product details were saved. In Shopify admin open Settings → Apps and sales channels → ` +
          `Develop apps → your app → Configuration, enable read_inventory, write_inventory and read_locations, ` +
          `then reconnect.`,
      };
    case 'shopify_unauthorized':
      return {
        title: 'Shopify connection expired',
        description:
          `Your Shopify session has expired, so stock for ${variantWord} couldn't be updated. ` +
          `Reconnect Shopify and try again. Other product details were saved.`,
        offerReconnect: true,
      };
    case 'no_shopify_location':
      return {
        title: "Couldn't update stock on Shopify",
        description:
          `Your Shopify store needs at least one location set up to track inventory. ` +
          `Open Shopify Admin → Settings → Locations, then try again. Other product details were saved.`,
      };
    case 'missing_inventory_item_id':
      return {
        title: "Can't track stock for this product",
        description:
          `Shopify isn't tracking inventory on ${variantWord}. Other product details were saved.`,
      };
    case 'network_error':
      return {
        title: "Couldn't reach Shopify",
        description:
          `Stock changes weren't sent because we couldn't connect to Shopify. ` +
          `Check your internet and try again. Other product details were saved.`,
      };
    case 'shopify_rejected':
    default:
      return {
        title: "Stock didn't sync to Shopify",
        description:
          `Shopify didn't accept the stock change for ${variantWord}. Other product details were saved. ` +
          `Try again, or contact support if this keeps happening.`,
      };
  }
};

interface EditShopifyProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: any | null;
  selectedAccountId: string | null;
  shopCurrency?: string;
  presentation?: 'dialog' | 'drawer';
  onSuccess: () => void;
}

type ImageEntry = ShopifyImageEntry;
type Variant = ShopifyVariant;

const STATUS_CONFIG = {
  active:   { label: 'Active',   color: 'bg-emerald-500' },
  draft:    { label: 'Draft',    color: 'bg-slate-400' },
  archived: { label: 'Archived', color: 'bg-orange-400' },
};

const parseJson = (field: any): any[] => {
  if (!field) return [];
  if (typeof field === 'string') { try { return JSON.parse(field); } catch { return []; } }
  return Array.isArray(field) ? field : [];
};

const parseTags = (raw: any): string[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string') {
    if (raw.startsWith('[')) { try { return JSON.parse(raw).filter(Boolean); } catch {} }
    return raw.split(',').map(t => t.trim()).filter(Boolean);
  }
  return [];
};

export const EditShopifyProductDialog: React.FC<EditShopifyProductDialogProps> = ({
  open, onOpenChange, product, selectedAccountId, shopCurrency = 'USD',
  presentation = 'dialog', onSuccess,
}) => {
  const navigate = useNavigate();
  const currencySymbol = getCurrencySymbol(shopCurrency);
  const resize = useFloatingDrawerResize(COMMERCE_V2_DRAWER_WIDTH);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionIsHtml, setDescriptionIsHtml] = useState(false);
  const [vendor, setVendor] = useState('');
  const [productType, setProductType] = useState('');
  const [status, setStatus] = useState('active');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [variants, setVariants] = useState<Variant[]>([]);
  const [optionName, setOptionName] = useState('');
  const [mainImages, setMainImages] = useState<ImageEntry[]>([]);
  const [mainImageUrls, setMainImageUrls] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState('');
  const [customVendor, setCustomVendor] = useState('');
  const [customProductType, setCustomProductType] = useState('');

  const [vendors, setVendors] = useState<string[]>([]);
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);

  const category = useShopifyCategoryField({ accountId: selectedAccountId });
  const taxonomyCategory = useShopifyTaxonomyCategoryField({
    accountId: selectedAccountId,
    open,
  });


  useEffect(() => {
    if (!product) return;
    setTitle(product.title || '');
    const seededDescription = seedDescriptionForEditing(
      product.description || product.body_html || '',
    );
    setDescription(seededDescription.value);
    setDescriptionIsHtml(seededDescription.isHtml);
    setVendor(product.vendor || '');
    setProductType(product.product_type || '');
    setStatus(product.status || 'active');
    setTags(parseTags(product.tags));
    setTagInput('');
    setMainImageUrls([]);
    setImageUrl('');
    setCustomVendor('');
    setCustomProductType('');

    const parsedImages: any[] = parseJson(product.images);
    const parsedVariants: any[] = parseJson(product.variants);
    const parsedOptions: any[] = parseJson(product.options);
    const firstOption = String(parsedOptions?.[0]?.name || '');
    setOptionName(firstOption === 'Title' ? '' : firstOption);

    setMainImages(
      parsedImages
        .filter(img => !img.variant_ids || img.variant_ids.length === 0)
        .map(img => ({ preview: img.src, existingId: img.id, variantIds: [] }))
    );

    setVariants(
      parsedVariants.length > 0
        ? parsedVariants.map(v => {
            const variantImg = v.image_id
              ? parsedImages.find(img => String(img.id) === String(v.image_id))
              : null;
            return {
              id: v.id,
              option1: v.option1 && v.option1 !== 'Default Title' ? v.option1 : '',
              sku: v.sku || '',
              price: v.price || '',
              compare_at_price: v.compare_at_price && Number(v.compare_at_price) > Number(v.price) ? String(v.compare_at_price) : '',
              inventory_quantity: v.inventory_quantity ?? 0,
              image: variantImg
                ? { preview: variantImg.src, existingId: variantImg.id }
                : null,
            };
          })
        : [emptyShopifyVariant()]
    );
  }, [product]);

  useEffect(() => {
    if (!open || !selectedAccountId) return;
    setMetaLoading(true);
    shopifyApi.getStoreMeta(selectedAccountId)
      .then(meta => {
        setVendors(meta.vendors || []);
        setProductTypes(meta.productTypes || []);
        category.setCollections(meta.collections || []);
        category.setSmartCollectionCount((meta.smartCollections || []).length);
      })
      .finally(() => setMetaLoading(false));
  }, [open, selectedAccountId]);

  useEffect(() => {
    if (!open || !selectedAccountId || !product?.shopify_product_id) return;
    let cancelled = false;
    shopifyApi
      .getProductCollections(selectedAccountId, String(product.shopify_product_id))
      .then((ids) => {
        if (cancelled) return;
        category.setCollectionId(ids.length ? String(ids[0]) : '');
      })
      .catch(() => {
        if (cancelled) return;
        category.setCollectionId('');
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedAccountId, product?.shopify_product_id]);

  useEffect(() => {
    taxonomyCategory.clearTaxonomyCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.shopify_product_id]);

  const readFile = (file: File): Promise<ImageEntry> =>
    new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) { reject(new Error('Not an image')); return; }
      if (file.size > 20 * 1024 * 1024) { reject(new Error(`${file.name} exceeds 20MB`)); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const r = reader.result as string;
        resolve({ file, preview: r, base64: r.split(',')[1] });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const addMainFiles = async (files: FileList | null) => {
    if (!files) return;
    const entries = await Promise.all(
      Array.from(files).map(f => readFile(f).catch(err => { toast({ title: err.message, variant: 'destructive' }); return null; }))
    );
    setMainImages(prev => [...prev, ...(entries.filter(Boolean) as ImageEntry[])]);
  };

  const addTag = (raw: string) => {
    const t = raw.trim().replace(/,+$/, '');
    if (!t || tags.includes(t)) return;
    setTags(prev => [...prev, t]);
    setTagInput('');
  };
  const removeTag = (index: number) =>
    setTags((prev) => prev.filter((_, i) => i !== index));
  const removeMainImage = (index: number) =>
    setMainImages((prev) => prev.filter((_, i) => i !== index));
  const removeMainImageUrl = (index: number) =>
    setMainImageUrls((prev) => prev.filter((_, i) => i !== index));
  const addImageUrl = () => {
    const raw = imageUrl.trim();
    const problem = validateProductImageUrl(raw);
    if (problem) {
      toast({ title: 'Unsupported image URL', description: problem, variant: 'warning' });
      return;
    }
    setMainImageUrls((prev) => (prev.includes(raw) ? prev : [...prev, raw]));
    setImageUrl('');
  };
  const onTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
    else if (e.key === 'Backspace' && !tagInput && tags.length) setTags(prev => prev.slice(0, -1));
  };

  const updateVariant = (index: number, field: keyof Variant, value: any) =>
    setVariants(prev => { const next = [...prev]; next[index] = { ...next[index], [field]: value }; return next; });

  const setVariantImage = async (index: number, files: FileList | null) => {
    if (!files?.[0]) return;
    try { updateVariant(index, 'image', await readFile(files[0])); }
    catch (err: any) { toast({ title: err.message, variant: 'destructive' }); }
  };

  const addVariant = () => { setVariants(prev => [...prev, emptyShopifyVariant()]); };
  const removeVariant = (i: number) => { if (variants.length > 1) setVariants(prev => prev.filter((_, idx) => idx !== i)); };

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!product || !selectedAccountId) return;
    const variantProblem = shopifyVariantProblem(variants, optionName);
    if (variantProblem) {
      toast({ title: variantProblem, variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const newMainImages = mainImages.filter(img => img.base64).map(img => ({ attachment: img.base64 }));

      const variantImagesPayload = variants
        .map(v => {
          if (!v.image || !v.id) return null;
          if (v.image.base64) {
            return { attachment: v.image.base64, variant_ids: [v.id] };
          } else if (v.image.existingId) {
            return { id: v.image.existingId, variant_ids: [v.id] };
          }
          return null;
        })
        .filter(Boolean);

      /*
        Images for variants this save CREATES. They have no Shopify id yet, so
        they cannot ride in `images[].variant_ids`; the server attaches each one
        once Shopify has created the variant. `variantIndex` is the variant's
        place in the list sent (the payload keeps the form's order), and
        `option1` lets the server check it matched the right one.
      */
      const newVariantImages = variants
        .map((v, i) =>
          !v.id && v.image?.base64
            ? {
                variantIndex: i,
                option1: v.option1.trim() || 'Default Title',
                attachment: v.image.base64,
              }
            : null,
        )
        .filter(Boolean);

      const productData: any = {
        title,
        body_html: descriptionToHtml(description),
        vendor: vendor === '__custom__' ? customVendor.trim() : vendor,
        product_type:
          productType === '__custom__' ? customProductType.trim() : productType,
        status,
        tags: tags.join(', '),
        collectionIds: category.collectionId ? [Number(category.collectionId)] : [],
        ...(taxonomyCategory.taxonomyCategoryId
          ? { categoryId: taxonomyCategory.taxonomyCategoryId }
          : {}),
        ...shopifyVariantsPayload(variants, optionName),
        ...(newVariantImages.length ? { variantImages: newVariantImages } : {}),
      };

      const originalImageCount = parseJson(product.images).length;
      const urlImages = mainImageUrls.map((url) => ({ src: url }));
      const hasNewMainImages = newMainImages.length > 0 || urlImages.length > 0;
      const removedExisting = mainImages.filter(img => img.existingId).length !== originalImageCount;

      const mainImagePayload = mainImages
        .map((img) =>
          img.base64
            ? { attachment: img.base64 }
            : img.existingId
              ? { id: img.existingId }
              : null,
        )
        .filter(Boolean);

      if (hasNewMainImages || removedExisting || variantImagesPayload.length) {
        productData.images = [
          ...mainImagePayload,       // the grid, in the order it is shown
          ...urlImages,              // images added by URL — Shopify fetches src
          ...variantImagesPayload,   // new variant images with variant_ids
        ];
      }

      const result: any = await shopifyApi.updateProduct(selectedAccountId, product.shopify_product_id, productData);

      const inventoryFailures: Array<{ variantId: string; reason: string; detail?: string }> =
        Array.isArray(result?.inventoryFailures) ? result.inventoryFailures : [];

      if (inventoryFailures.length > 0) {
        console.error(
          '[ShopifyProduct] Inventory sync failed:',
          inventoryFailures.map(f => ({ variantId: f.variantId, reason: f.reason, detail: f.detail })),
        );

        const friendly = mapInventoryFailureToFriendlyMessage(
          inventoryFailures[0]?.reason || 'shopify_rejected',
          inventoryFailures.length,
        );
        toast({
          title: friendly.title,
          description: friendly.description,
          variant: 'destructive',
          ...(friendly.offerReconnect
            ? {
                action: (
                  <ToastAction
                    altText="Reconnect Shopify"
                    onClick={() => navigate('/home/shopify')}
                  >
                    Reconnect Shopify
                  </ToastAction>
                ),
              }
            : {}),
        });
      } else {
        toast({ variant: "success", title: 'Product Updated', description: 'Changes synced to Shopify.' });
      }
      const variantImageFailures: Array<{ variantIndex: number; reason: string }> =
        Array.isArray(result?.variantImageFailures) ? result.variantImageFailures : [];
      if (variantImageFailures.length > 0) {
        // The product saved; only some new-variant images did not attach.
        toast({
          title: `${variantImageFailures.length === 1 ? 'A variant image' : `${variantImageFailures.length} variant images`} could not be added`,
          description: 'The product was saved. Open it again and re-add the image.',
          variant: 'warning',
        });
      }
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Update Failed' } });
    } finally {
      setLoading(false);
    }
  };

  const statusSelect = (
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 text-xs border-slate-200 w-28 font-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
              <SelectItem key={val} value={val} className="text-xs">
                <div className="flex items-center gap-2">
                  <span className={cn('w-1.5 h-1.5 rounded-full', cfg.color)} />
                  {cfg.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
  );

  const form: ShopifyProductForm = {
    title, setTitle,
    description, setDescription,
    descriptionIsHtml,
    vendor, setVendor,
    customVendor, setCustomVendor,
    productType, setProductType,
    customProductType, setCustomProductType,
    status, setStatus,
    tags, tagInput, setTagInput, addTag, removeTag, onTagKeyDown,
    variants, optionName, setOptionName, variantImageRequiresId: false, addVariant, removeVariant, updateVariant, setVariantImage,
    mainImages, removeMainImage,
    addMainFiles,
    mainImageUrls, imageUrl, setImageUrl, addImageUrl, removeMainImageUrl,
    vendors, productTypes,
    collectionId: category.collectionId,
    setCollectionId: category.setCollectionId,
    collections: category.collections,
    smartCollectionCount: category.smartCollectionCount,
    creatingCategory: category.creatingCategory,
    openCreateCategory: category.openCreateCategory,
    cancelCreateCategory: category.cancelCreateCategory,
    newCategory: category.newCategory,
    setNewCategory: category.setNewCategory,
    savingCategory: category.savingCategory,
    createCategory: category.createCategory,
    taxonomyCategoryId: taxonomyCategory.taxonomyCategoryId,
    taxonomyCategoryName: taxonomyCategory.taxonomyCategoryName,
    taxonomyCategoryResults: taxonomyCategory.taxonomyCategoryResults,
    taxonomyCategorySearching: taxonomyCategory.taxonomyCategorySearching,
    searchTaxonomyCategories: taxonomyCategory.searchTaxonomyCategories,
    selectTaxonomyCategory: taxonomyCategory.selectTaxonomyCategory,
    clearTaxonomyCategory: taxonomyCategory.clearTaxonomyCategory,
    metaLoading,
    currencySymbol,
    loading,
    loadingMsg: '',
    singleVariant: false,
    canSubmit: Boolean(title.trim()),
    reset: () => {},
    submit: async () => ({ ok: false }),
  };

  const bodyForm = (
    <div className="p-6">
      <ShopifyProductFormFields form={form} />
    </div>
  );

  const imageCount = mainImages.length + mainImageUrls.length;

  const footerActions = (
      <div className="flex items-center justify-between w-full">
        <p className="text-xs text-slate-400">
          {[
            imageCount > 0 && `${imageCount} image${imageCount > 1 ? 's' : ''}`,
            variants.length > 1 && `${variants.length} variants`,
            tags.length > 0 && `${tags.length} tag${tags.length > 1 ? 's' : ''}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
          <Button onClick={onSubmit} disabled={loading || !title.trim()} className="bg-blue-600 hover:bg-blue-700 text-white">
            {loading
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              : <><Save className="mr-2 h-4 w-4" />Save Changes</>}
          </Button>
        </div>
      </div>
  );

  if (presentation === 'drawer') {
    return (
      <FloatingDrawer
        open={open}
        onOpenChange={onOpenChange}
        {...resize.drawerProps}
        title="Edit Product"
      >
        <FloatingDrawerHeader
          title="Edit Product"
          subtitle="Changes will be synced to Shopify"
          icon={<Package className="h-4.5 w-4.5" />}
          right={statusSelect}
        />
        <FloatingDrawerBody className="px-0 py-0">{bodyForm}</FloatingDrawerBody>
        <FloatingDrawerFooter>{footerActions}</FloatingDrawerFooter>
      </FloatingDrawer>
    );
  }

  return (
    <ScrollableDialog open={open} onOpenChange={onOpenChange}>
      <ScrollableDialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden flex flex-col">

        <ScrollableDialogHeader className="px-6 py-4 border-b bg-white shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <ScrollableDialogTitle className="text-base font-semibold">Edit Product</ScrollableDialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Changes will be synced to Shopify</p>
              </div>
            </div>
            <div className="flex items-center gap-2">{statusSelect}</div>
          </div>
        </ScrollableDialogHeader>

        <div className="overflow-y-auto flex-1">{bodyForm}</div>

        <ScrollableDialogFooter className="px-6 py-4 border-t bg-white shrink-0">
          {footerActions}
        </ScrollableDialogFooter>
      </ScrollableDialogContent>
    </ScrollableDialog>
  );
};

export default EditShopifyProductDialog;
